import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

/**
 * Auth Store — manages authentication state, accounts, and active session.
 * Replaces the Vuex auth store (store/auth.js)
 *
 * Note: This store is created as part of the Pinia migration.
 * API.AuthInfo.ensure() wrappers have been removed for now — raw data is
 * returned from getters until the old API layer is fully retired.
 */
export const useAuthStore = defineStore('auth', () => {
  const DEFAULT_SCHEDULE_VIEW = '/schedule/day'
  const VALID_SCHEDULE_VIEWS = new Set([
    '/schedule/day',
    '/schedule/week',
    '/schedule/month',
    '/schedule/timetable',
    '/schedule/grid',
  ])

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const ready = ref<boolean>(true)

  const auth = ref<{ email: string; redirect: string | null; invite: boolean }>({
    email: '',
    redirect: null,
    invite: false,
  })

  const active = ref<string | null>(null)

  const accounts = ref<Record<string, any>>({})

  const guids = ref<string[]>([])

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * Resolve the account for a given GUID, falling back to the active account.
   */
  function getAccount(guid?: string | null): any | undefined {
    if (guid && accounts.value[guid]) {
      return accounts.value[guid]
    }
    if (active.value && accounts.value[active.value]) {
      return accounts.value[active.value]
    }
    return undefined
  }

  function normalizeScheduleView(view: unknown): string {
    if (typeof view === 'string' && VALID_SCHEDULE_VIEWS.has(view)) {
      return view
    }

    if (view && typeof view === 'object') {
      const routeLike = view as { value?: unknown; path?: unknown }
      const raw = routeLike.value ?? routeLike.path
      if (typeof raw === 'string' && VALID_SCHEDULE_VIEWS.has(raw)) {
        return raw
      }
    }

    return DEFAULT_SCHEDULE_VIEW
  }

  // ---------------------------------------------------------------------------
  // Computed (replaces Vuex getters)
  // ---------------------------------------------------------------------------

  /** Raw info object for the active account. */
  const info = computed<any | null>(() => {
    const account = accounts.value?.[active.value as string]
    return account ? account.info : null
  })

  /** Whether the active account is locked. */
  const locked = computed<boolean>(() => {
    const account = accounts.value?.[active.value as string]
    return account ? !!account.locked : false
  })

  /** Home path for the active account. */
  const home = computed<string | null>(() => {
    const account = accounts.value?.[active.value as string]
    return account ? account.home : null
  })

  /** Sort preferences for the active account. */
  const sort = computed<Record<string, any>>(() => {
    const account = accounts.value?.[active.value as string]
    return account ? account.sort : {}
  })

  /** The full active account object. */
  const activeAccount = computed<any | undefined>(() => {
    return accounts.value?.[active.value as string]
  })

  /** The raw active GUID string (used by other stores for the Dexie compound key). */
  const activeGuid = computed<string | null>(() => active.value)

  /** Login attempts for the active account. */
  const attempts = computed<number>(() => {
    const account = accounts.value?.[active.value as string]
    return account?.attempts ?? 0
  })

  /** Panel mode for the active account. */
  const panel = computed<string | undefined>(() => {
    const account = accounts.value?.[active.value as string]
    return account?.panel
  })

  /** All accounts as an array (raw values, no API.AuthInfo wrapping). */
  const accountsList = computed<any[]>(() => {
    return Object.values(accounts.value)
  })

  /** Schedule view path for the active account. */
  const scheduleView = computed<string>(() => {
    const account = accounts.value?.[active.value as string]
    return normalizeScheduleView(account?.scheduleView)
  })

  /** Parsed grants map for the active account: { [model]: action[] } */
  const grants = computed<Record<string, string[]>>(() => {
    const account = accounts.value?.[active.value as string]
    if (!account) return {}

    const grantPaths: string[] | undefined = account.info?.employee?.role?.grants
    if (!grantPaths) return {}

    const map: Record<string, string[]> = {}
    for (const grant of grantPaths) {
      const item = grant.split('?')[0]
      const [, , model, action] = item.split('/')
      if (model) {
        if (!map[model]) map[model] = []
        map[model].push(action)
      }
    }
    return map
  })

  // ---------------------------------------------------------------------------
  // Actions (replaces Vuex mutations — direct state modifications)
  // ---------------------------------------------------------------------------

  function setAccountInfo({ guid, info: newInfo }: { guid?: string; info: any }) {
    const account = getAccount(guid)
    if (account) account.info = newInfo
  }

  function setActive(guid: string) {
    active.value = guid
    guids.value = Object.keys(accounts.value)
  }

  function setReady(value: boolean) {
    ready.value = value
  }

  function setLock({ guid, locked: isLocked }: { guid?: string; locked: boolean }) {
    const account = getAccount(guid)
    if (account) account.locked = isLocked
  }

  function setHome({ guid, home: homePath }: { guid?: string; home: string | null }) {
    const account = getAccount(guid)
    if (account) account.home = homePath
  }

  function setSort({ guid, model, value }: { guid?: string; model: string; value: any }) {
    const account = getAccount(guid)
    if (account) {
      if (!account.sort) account.sort = {}
      account.sort[model] = value
    }
  }

  function setHistory({ guid, path }: { guid?: string; path?: string }) {
    const account = getAccount(guid)
    if (account) account.history = path || '/'
  }

  function setAttempts({ guid, value }: { guid?: string; value?: number }) {
    const account = getAccount(guid)
    if (account) account.attempts = value || 0
  }

  function setLastActive({ guid, value = Date.now() }: { guid?: string; value?: number }) {
    const account = getAccount(guid)
    if (account) account.lastActiveAt = value
  }

  function setPanel({ guid, mode }: { guid?: string; mode?: string }) {
    const account = getAccount(guid)
    if (account) account.panel = mode || 'panel'
  }

  function setScheduleView({ guid, view }: { guid?: string; view?: unknown }) {
    const account = getAccount(guid)
    if (account) account.scheduleView = normalizeScheduleView(view)
  }

  function addAccount(account: any) {
    const guid: string = account.guid
    accounts.value = { ...accounts.value, [guid]: { ...account } }
    if (guid) active.value = guid
    guids.value = Object.keys(accounts.value)
  }

  function removeAccount(account: any) {
    const guid: string | undefined = account?.guid
    if (guid) {
      const { [guid]: _removed, ...rest } = accounts.value
      accounts.value = rest
    }
    guids.value = Object.keys(accounts.value)
  }

  /** Replaces the Vue.set-based `set` mutation — direct assignment is reactive in Pinia. */
  function setAuthField({ key, value }: { key: string; value: any }) {
    ;(auth.value as any)[key] = value
  }

  /** Replace the entire auth object (used by login/reset pages). */
  function setAuth(value: { email: string; redirect: string | null; invite: boolean }) {
    auth.value = value
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  return {
    // State
    ready,
    auth,
    active,
    accounts,
    guids,

    // Computed
    info,
    locked,
    home,
    sort,
    activeAccount,
    activeGuid,
    attempts,
    panel,
    accountsList,
    scheduleView,
    grants,

    // Helpers
    getAccount,

    // Actions
    setAccountInfo,
    setActive,
    setReady,
    setLock,
    setHome,
    setSort,
    setHistory,
    setAttempts,
    setLastActive,
    setPanel,
    setScheduleView,
    addAccount,
    removeAccount,
    setAuthField,
    setAuth,
  }
})
